import { LayoutDashboard, Receipt, Wallet, Settings } from "lucide-react";
import { cn } from "@/ui/components/primitives/utils";
import { useLocation, useNavigate } from "react-router-dom";
import { Badge } from "@/ui/components/primitives/badge";

const primaryNavItems = [
  { page: "dashboard", path: "/dashboard", icon: LayoutDashboard, label: "Home" },
  { page: "transactions", path: "/transactions", icon: Receipt, label: "Activity" },
];

const secondaryNavItems = [
  { page: "wallet", path: "/wallet", icon: Wallet, label: "Wallet" },
  { page: "settings", path: "/settings", icon: Settings, label: "More" },
];

export function MobileNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const getNavItemClass = (isActive: boolean) => cn(
    "flex flex-col items-center justify-center gap-1 transition-all duration-200 touch-manipulation",
    "min-h-[44px] min-w-[44px] py-2 px-3 rounded-lg", // Touch-friendly size
    isActive
      ? "text-primary"
      : "text-muted-foreground hover:text-foreground"
  );

  const isPrimaryActive = primaryNavItems.some(item => pathname === item.path);
  const isSecondaryActive = secondaryNavItems.some(item => pathname === item.path);

  return (
    <>
      {/* Main Bottom Navigation */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-sm border-t border-border/50"
        role="navigation"
        aria-label="Mobile navigation"
      >
        <div className="grid grid-cols-4 h-[60px]">
          {/* Primary Navigation */}
          {primaryNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = pathname === item.path;

            return (
              <button
                key={item.page}
                onClick={() => navigate(item.path)}
                className={getNavItemClass(isActive)}
                aria-label={item.label}
                aria-current={isActive ? "page" : undefined}
              >
                <Icon className="h-5 w-5" strokeWidth={1.5} />
                <span className="text-xs font-medium leading-tight">{item.label}</span>
              </button>
            );
          })}

          {/* Secondary Navigation */}
          {secondaryNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = pathname === item.path;

            return (
              <button
                key={item.page}
                onClick={() => navigate(item.path)}
                className={getNavItemClass(isActive)}
                aria-label={item.label}
                aria-current={isActive ? "page" : undefined}
              >
                <Icon className="h-5 w-5" strokeWidth={1.5} />
                <span className="text-xs font-medium leading-tight">{item.label}</span>
              </button>
            );
          })}
        </div>

        {/* Active Page Indicator */}
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary transform transition-transform duration-300">
          <div
            className="h-full bg-primary"
            style={{
              width: '25%',
              transform: `translateX(${
                primaryNavItems.findIndex(item => pathname === item.path) !== -1
                  ? `${primaryNavItems.findIndex(item => pathname === item.path) * 100}%`
                  : secondaryNavItems.findIndex(item => pathname === item.path) !== -1
                      ? `${(2 + secondaryNavItems.findIndex(item => pathname === item.path)) * 100}%`
                      : '0%' // Default to first item
              })`
            }}
          />
        </div>
      </nav>

      {/* Safe Area Padding for iOS */}
      <div className="md:hidden h-[60px]" />
    </>
  );
}
